/**
 * Utilities for reading Vite manifest to generate dynamic modulepreload links
 * Part of the network dependency chain optimization
 */

interface ManifestEntry {
  file: string;
  src?: string;
  name?: string;
  isEntry?: boolean;
  imports?: string[];
  css?: string[];
}

interface ViteManifest {
  [key: string]: ManifestEntry;
}

let manifestCache: ViteManifest | null = null;

/**
 * Reads the Vite manifest and returns the mapping of entry points to actual files
 * This eliminates the need for hardcoded file hashes that change with each build
 */
function loadManifest(): ViteManifest | null {
  // In development, return null since we don't need modulepreload
  if (typeof window !== 'undefined' && location.hostname === 'localhost') {
    return null;
  }

  // Return cached manifest if available
  if (manifestCache) {
    return manifestCache;
  }

  try {
    // For production builds, read the manifest synchronously
    // Note: This is a simplified approach for static sites
    if (typeof window === 'undefined') {
      // SSR context - try to read from filesystem
      const fs = require('fs');
      const path = require('path');
      const manifestPath = path.join(process.cwd(), 'dist/.vite/manifest.json');
      
      if (fs.existsSync(manifestPath)) {
        const manifestContent = fs.readFileSync(manifestPath, 'utf-8');
        manifestCache = JSON.parse(manifestContent);
        return manifestCache;
      }
    }

    // Client-side: For static sites, we need to embed the manifest
    // This is a limitation of the current approach
    return null;
  } catch (error) {
    console.warn('Failed to read manifest for modulepreload optimization:', error);
    return null;
  }
}

/**
 * Gets the critical JavaScript modules for blog pages from the manifest
 * Returns the actual file paths with correct hashes
 */
export function getBlogPageCriticalModules(): string[] {
  // In development, return empty array
  if (typeof window !== 'undefined' && location.hostname === 'localhost') {
    return [];
  }

  const manifest = loadManifest();
  if (!manifest) {
    return [];
  }

  const criticalModules: string[] = [];

  // Add the main entry point
  if (manifest['index.html']) {
    criticalModules.push(`/${manifest['index.html'].file}`);
  }

  // Add the Blog page component
  if (manifest['src/pages/Blog.tsx']) {
    criticalModules.push(`/${manifest['src/pages/Blog.tsx'].file}`);
  }

  // Add BlogImage component
  const blogImageKey = Object.keys(manifest).find(key => 
    manifest[key].name === 'BlogImage'
  );
  if (blogImageKey && manifest[blogImageKey]) {
    criticalModules.push(`/${manifest[blogImageKey].file}`);
  }

  // Add PostCard component
  const postCardKey = Object.keys(manifest).find(key => 
    manifest[key].name === 'PostCard'
  );
  if (postCardKey && manifest[postCardKey]) {
    criticalModules.push(`/${manifest[postCardKey].file}`);
  }

  // Add responsive image utility
  const responsiveImageKey = Object.keys(manifest).find(key => 
    manifest[key].name === 'responsive-image'
  );
  if (responsiveImageKey && manifest[responsiveImageKey]) {
    criticalModules.push(`/${manifest[responsiveImageKey].file}`);
  }

  return criticalModules;
}

/**
 * Alternative approach: Return hardcoded paths for production builds
 * This is more reliable for static sites where manifest reading is complex
 */
export function getBlogPageCriticalModulesStatic(): string[] {
  // In development, return empty array
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return [];
  }

  // For production, return the current build's actual file names
  // These will be updated by the build process or a post-build script
  const criticalModules = [
    '/assets/index-B2fX2eUb.js',        // index - AUTO-UPDATED
    '/assets/Blog-CovEDWsx.js',        // Blog - AUTO-UPDATED
    '/assets/BlogImage-Cl4My5sY.js',        // BlogImage - AUTO-UPDATED
    '/assets/PostCard-BZIKCLC6.js',        // PostCard - AUTO-UPDATED
    '/assets/responsive-image-ks09yDAf.js'  // Keep existing - utility module
  ];
  
  return criticalModules;
} 